import { createColumnHelper } from '@tanstack/react-table'
import { useEffect, useState } from 'react'
import { DrilldownScaffold } from '~/components/button'
import { DateCell } from '~/features/table/cells/date'
import { InlineEditCell } from '~/features/table/cells/inline-edit'
import { createPlacehodlerData } from '~/features/table/ui/table'
import { FlagMutators } from '~/mutators/flags'
import { RouterOutput } from '~/services/trpc'

import {
  AlertDialog,
  AlertDialogTrigger,
  Button,
  ColorPickerV2,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from '@hap/components'
import { FLAG_COLORS } from '@hap/utils'

export const colorOptions = Object.values(FLAG_COLORS)

interface Row extends Omit<RouterOutput['flags']['list'][0], 'createdAt' | 'updatedAt'> {
  editing?: boolean
  onBlur?: () => void
  createdAt?: string
  updatedAt?: string
}

export const PLACEHOLDER_DATA = createPlacehodlerData<Row>()

const columnHelper = createColumnHelper<Row>()

export const columns = [
  columnHelper.accessor('color', {
    header: '',
    size: 5,
    enableSorting: false,
    cell: ({ getValue, row }) => {
      const [colorVisibility, setColorVisibility] = useState(false)
      const { update, variables: updateVars } = FlagMutators.useUpdate()
      const { create } = FlagMutators.useCreate({
        onSuccess: row.original.onBlur,
      })

      const color = updateVars?.values?.color || getValue()

      return (
        <DropdownMenu open={colorVisibility} onOpenChange={setColorVisibility}>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <div className="flex aspect-square h-7 cursor-pointer items-center justify-center">
                  <div
                    className={cn(
                      'group-hover/row:ring-input-secondary-background ratio-square h-4 w-4 shrink-0 rounded-full group-hover/row:ring-4',
                      {
                        'ring-input-secondary-background ring-4': colorVisibility,
                      },
                    )}
                    style={{ backgroundColor: color }}
                  />
                </div>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent>Change color</TooltipContent>
          </Tooltip>

          <DropdownMenuContent sideOffset={12} className="min-w-80">
            <ColorPickerV2
              value={color}
              options={colorOptions}
              onSelect={(value) => {
                if (row.original.id) {
                  update({
                    flagId: row.original.id,
                    values: { color: value },
                  })
                } else {
                  // For new rows, create with just the color (name will come later)
                  create({
                    color: value,
                    name: row.original.name || 'New Flag',
                  })
                }
                setColorVisibility(false)
              }}
            />
          </DropdownMenuContent>
        </DropdownMenu>
      )
    },
  }),
  columnHelper.accessor('name', {
    header: 'Name',
    enableSorting: true,
    size: 100,
    cell: ({ getValue, row }) => {
      const [editValue, setEditValue] = useState<string | null>(null)
      const { update, variables: updateVars } = FlagMutators.useUpdate({
        onSuccess: row.original.onBlur,
      })
      const { create } = FlagMutators.useCreate({
        onSuccess: row.original.onBlur,
      })

      useEffect(() => {
        if (row.original.editing && editValue === null) {
          setEditValue(getValue())
        }
      }, [row.original.editing])

      const displayName = updateVars?.values?.name || getValue()
      const color = row.original.color

      return (
        <InlineEditCell
          value={displayName}
          editValue={editValue}
          onEditValueChange={setEditValue}
          editTargetId={`${row.original.id}-name`}
          placeholder="Enter flag name"
          onSubmit={(value) => {
            if (row.original.id) {
              update({ flagId: row.original.id, values: { name: value } })
            } else {
              create({ color, name: value })
            }
          }}
          onCancel={row.original.onBlur}
        />
      )
    },
  }),
  columnHelper.accessor((value) => ({ usage: value.usage, id: value.id }), {
    header: 'Interactions',
    size: 5,
    enableSorting: true,
    cell: ({ getValue }) => {
      const value = getValue()
      return value.usage ? (
        <Tooltip>
          <TooltipTrigger>
            <DrilldownScaffold
              filters={[{ field: 'flagId', op: 'in', values: [value.id] }]}
              className="hover:bg-accent whitespace-nowrap rounded-md p-2"
            >
              {value.usage.toLocaleString()}
            </DrilldownScaffold>
          </TooltipTrigger>
          <TooltipContent>View interactions</TooltipContent>
        </Tooltip>
      ) : null
    },
  }),
  columnHelper.accessor('updatedAt', {
    header: 'Last Modified',
    size: 5,
    enableSorting: true,
    cell: DateCell(),
  }),
  columnHelper.display({
    id: 'actions',
    size: 2,
    cell: ({ row }) => {
      const [menuOpen, setMenuOpen] = useState(false)
      const color = row.original.color
      const { update } = FlagMutators.useUpdate()
      const { destroy } = FlagMutators.useDestroy()

      const isNew = row.original.id === ''
      return isNew ? null : (
        <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" icon="dots-three-outline" size="icon" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              icon="pencil-simple"
              onClick={() => {
                const selector = `[data-edit-target="${row.original.id}-name"]`
                const elements = document.querySelectorAll<HTMLElement>(selector)
                if (elements.length) elements[0].click()
                else console.warn('No element found with selector: ' + selector)
                setMenuOpen(false)
              }}
            >
              Rename flag
            </DropdownMenuItem>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger icon="paint-brush">
                Change color
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent sideOffset={12}>
                <ColorPickerV2
                  value={color}
                  options={colorOptions}
                  onSelect={(value) => {
                    update({
                      flagId: row.original.id,
                      values: { color: value },
                    })
                    setMenuOpen(false)
                  }}
                />
              </DropdownMenuSubContent>
            </DropdownMenuSub>

            <AlertDialog
              title="Delete flag"
              description="Please confirm you want to delete this flag."
              action={{
                value: 'Delete',
                variant: 'destructive',
                onClick: () => {
                  destroy(row.original.id)
                  setMenuOpen(false)
                },
              }}
            >
              <AlertDialogTrigger asChild>
                <DropdownMenuItem
                  variant="destructive"
                  onSelect={(e) => {
                    e.preventDefault()
                  }}
                  icon="trash"
                >
                  Delete flag...
                </DropdownMenuItem>
              </AlertDialogTrigger>
            </AlertDialog>
          </DropdownMenuContent>
        </DropdownMenu>
      )
    },
  }),
]
